<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import aiReviewsAPI from 'dashboard/api/aiReviews';
import Button from 'dashboard/components-next/button/Button.vue';
import PageHeader from '../components/PageHeader.vue';
import PagePagination from '../components/PagePagination.vue';
import StatusBadge from '../components/StatusBadge.vue';
import { formatDateTime, getApiErrorMessage } from '../utils';

const { t } = useI18n();

const batches = ref([]);
const meta = ref({ current_page: 1, total_pages: 1, total_count: 0 });
const isLoading = ref(false);

const loadBatches = async (page = meta.value.current_page) => {
  try {
    isLoading.value = true;
    const { data } = await aiReviewsAPI.getBatches(page);
    batches.value = data.payload || [];
    meta.value = data.meta || meta.value;
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => loadBatches(1));
</script>

<template>
  <PageHeader
    :title="t('AI_REVIEWS.BATCHES.TITLE')"
    :description="t('AI_REVIEWS.BATCHES.DESCRIPTION')"
  >
    <template #actions>
      <Button
        :label="t('AI_REVIEWS.COMMON.REFRESH')"
        icon="i-lucide-refresh-cw"
        color="slate"
        variant="faded"
        :is-loading="isLoading"
        @click="loadBatches()"
      />
      <RouterLink :to="{ name: 'ai_review_batch_new' }">
        <Button
          :label="t('AI_REVIEWS.NEW_BATCH.LAUNCH')"
          icon="i-lucide-plus"
        />
      </RouterLink>
    </template>
  </PageHeader>

  <div
    v-if="isLoading && !batches.length"
    class="p-10 text-center text-n-slate-11"
  >
    {{ t('AI_REVIEWS.COMMON.LOADING') }}
  </div>
  <div
    v-else-if="!batches.length"
    class="p-10 text-sm text-center border border-dashed rounded-xl border-n-weak text-n-slate-11"
  >
    {{ t('AI_REVIEWS.BATCHES.EMPTY') }}
  </div>
  <template v-else>
    <div class="overflow-x-auto border rounded-xl border-n-weak">
      <table class="w-full min-w-[980px] text-sm">
        <thead class="text-left bg-n-alpha-1 text-n-slate-11">
          <tr>
            <th class="p-3 font-medium">
              {{ t('AI_REVIEWS.BATCHES.CREATED') }}
            </th>
            <th class="p-3 font-medium">
              {{ t('AI_REVIEWS.BATCHES.REQUESTED_BY') }}
            </th>
            <th class="p-3 font-medium">
              {{ t('AI_REVIEWS.BATCHES.POLICY') }}
            </th>
            <th class="p-3 font-medium">{{ t('AI_REVIEWS.BATCHES.MODEL') }}</th>
            <th class="p-3 font-medium">{{ t('AI_REVIEWS.BATCHES.TOTAL') }}</th>
            <th class="p-3 font-medium">
              {{ t('AI_REVIEWS.BATCHES.COMPLETED') }}
            </th>
            <th class="p-3 font-medium">
              {{ t('AI_REVIEWS.BATCHES.FAILED') }}
            </th>
            <th class="p-3 font-medium">
              {{ t('AI_REVIEWS.BATCHES.STATUS') }}
            </th>
            <th class="p-3 font-medium" />
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr v-for="batch in batches" :key="batch.id">
            <td class="p-3 text-n-slate-11">
              {{ formatDateTime(batch.created_at) }}
            </td>
            <td class="p-3 text-n-slate-12">{{ batch.requester.name }}</td>
            <td class="p-3 text-n-slate-12">
              {{ batch.policy.name }} ·
              {{
                t('AI_REVIEWS.COMMON.VERSION', {
                  version: batch.policy.version,
                })
              }}
            </td>
            <td class="p-3 text-n-slate-12">{{ batch.model_profile.name }}</td>
            <td class="p-3 tabular-nums">{{ batch.total }}</td>
            <td class="p-3 tabular-nums">{{ batch.completed }}</td>
            <td class="p-3 tabular-nums">{{ batch.failed }}</td>
            <td class="p-3"><StatusBadge :status="batch.status" /></td>
            <td class="p-3 text-right">
              <RouterLink
                :to="{
                  name: 'ai_review_batch_show',
                  params: { batchId: batch.id },
                }"
              >
                <Button
                  :label="t('AI_REVIEWS.BATCHES.OPEN')"
                  color="slate"
                  variant="faded"
                  size="sm"
                />
              </RouterLink>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <PagePagination
      :current-page="meta.current_page"
      :total-pages="meta.total_pages"
      :total-count="meta.total_count"
      @change="loadBatches"
    />
  </template>
</template>
